import { lazy, Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { useRestaurant } from '@/contexts/RestaurantContext'
import { useGeneralSettings } from '@/features/general-settings/hooks/useGeneralSettings'
import { OrdersEditorProvider, useOrdersEditor } from '@/features/orders/hooks/useOrdersEditor'
import { DateNavigator } from '@/features/orders/components/DateNavigator'
import { OrderCard } from '@/features/orders/components/OrderCard'
import { KanbanColumn } from '@/features/orders/components/KanbanColumn'
import { OrderDetailInline } from '@/features/orders/components/OrderDetailInline'
import { MobileOrderDetailSheet } from '@/features/orders/components/MobileOrderDetailSheet'
import { OrdersEmptyState } from '@/features/orders/components/OrdersEmptyState'
import { OrdersSkeleton } from '@/features/orders/components/OrdersSkeleton'
import { CancelOrderDialog } from '@/features/orders/components/CancelOrderDialog'
import { DeclineOrderDialog } from '@/features/orders/components/DeclineOrderDialog'
import { SuperAdminNotSupportedError } from '@/features/orders/hooks/useOrderActions'
import { groupOrdersByAnswered } from '@/features/orders/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AlertTriangle, Loader2, RefreshCw, Search, SearchX, UtensilsCrossed, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { usePermissions } from '@/hooks/usePermissions'
import type { PosSetup } from '@/features/pos-setup/model'
import { CashRegisterContext } from '@/features/pos-ordering/CashRegisterContext'
import { fetchPosCash } from '@/features/pos-ordering/service'
import { ConnectionIndicator } from '@/features/orders/components/ConnectionIndicator'
import { RestaurantStatusToggle } from '@/features/orders/components/RestaurantStatusToggle'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { KANBAN_LAYOUT_ENABLED } from '@/config/featureFlags'
import { toast } from 'sonner'
import type { DeclineReason } from '@/features/orders/types'

const QuickAvailabilityPanel = lazy(() =>
  import('@/features/menu-editor/components/QuickAvailabilityPanel').then((module) => ({
    default: module.QuickAvailabilityPanel,
  }))
)

const PosOrderEntry = lazy(() =>
  import('@/features/pos-ordering/PosOrderEntry').then((module) => ({
    default: module.PosOrderEntry,
  }))
)

const PosCashManagement = lazy(() => import('@/features/pos-ordering/PosCashManagement').then(module => ({ default: module.PosCashManagement })))

function OrdersContent() {
  const { t } = useTranslation(['orders', 'menuEditor'])
  const { selectedRestaurant } = useRestaurant()
  const isMobile = useIsMobile()
  const { isOnline } = useOnlineStatus()
  const [isAvailabilityPanelOpen, setIsAvailabilityPanelOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const quickAvailabilityLabel = t('menuEditor:quickAvailability.title')
  const { settings: generalSettings } = useGeneralSettings(selectedRestaurant?.id ?? 0)
  const orderConfirmationEnabled = generalSettings?.orderConfirmationEnabled ?? false

  const {
    selectedDate, setSelectedDate, goToToday, goToPreviousDay, goToNextDay,
    orders, filteredOrders, orderCounts, selectedOrder,
    isLoading, isFetching, error,
    state, selectOrder, closeDetail,
    openCancelDialog, closeCancelDialog,
    openDeclineDialog, closeDeclineDialog,
    refresh,
    filters, setSearchQuery,
    updateOrderStatus, cancelOrder, confirmOrder, declineOrder,
    isUpdating, isCancelling, isConfirming, isDeclining,
  } = useOrdersEditor()

  const searchActive = filters.searchQuery.trim().length > 0

  // --- Action handlers ---

  const handleStatusChange = async (orderId: number, status: string) => {
    try {
      await updateOrderStatus(orderId, status)
      toast.success(t('toast.statusUpdated'))
    } catch (e) {
      if (e instanceof SuperAdminNotSupportedError) {
        toast.error(t('toast.superAdminNotSupported'))
      } else {
        toast.error(t('toast.statusError'))
      }
    }
  }

  const handleCancelOrder = async (reason?: string) => {
    if (!selectedOrder) return
    try {
      const res = await cancelOrder(selectedOrder.id, reason)
      if (res.refundInitiated && res.refundStatus === 'succeeded') {
        toast.success(t('toast.cancelSuccessRefunded'))
      } else if (res.refundInitiated) {
        toast.warning(t('toast.cancelSuccessRefundFailed'))
      } else {
        toast.success(t('toast.cancelSuccess'))
      }
      closeDetail()
    } catch (e) {
      if (e instanceof SuperAdminNotSupportedError) {
        toast.error(t('toast.superAdminNotSupported'))
      } else {
        toast.error(t('toast.cancelError'))
      }
    }
  }

  const handleConfirmOrder = async () => {
    if (!selectedOrder) return
    try {
      await confirmOrder(selectedOrder.id)
      toast.success(t('toast.confirmSuccess'))
    } catch { toast.error(t('toast.confirmError')) }
  }

  const handleDeclineOrder = async (reason: DeclineReason, customReason?: string) => {
    if (!selectedOrder) return
    try {
      await declineOrder(selectedOrder.id, reason, customReason)
      toast.success(t('toast.declineSuccess'))
    } catch { toast.error(t('toast.declineError')) }
  }

  // --- Kanban grouping ---
  const { open, answered } = groupOrdersByAnswered(filteredOrders)

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-3 px-3 sm:px-4 h-11 sm:h-12">
          <h1 className="font-display font-bold truncate text-base sm:text-lg tracking-tight shrink-0">{t('header.title')}</h1>

          {/* Day stats as quiet inline text — only rendered when meaningful */}
          <div className="flex items-center gap-3 text-sm tabular-nums min-w-0 truncate">
            {orderCounts.pending > 0 && (
              <span className="flex items-center gap-1.5 font-semibold text-status-warning shrink-0">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute h-1.5 w-1.5 rounded-full bg-status-warning animate-ping opacity-75" />
                  <span className="h-1.5 w-1.5 rounded-full bg-status-warning" />
                </span>
                {orderCounts.pending} {t('stats.pending')}
              </span>
            )}
            {orderCounts.active > 0 && (
              <span className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-status-success" />
                {orderCounts.active} {t('stats.active')}
              </span>
            )}
          </div>

          <div className="ms-auto flex items-center gap-2 sm:gap-3 shrink-0">
            <ConnectionIndicator isOnline={isOnline} isSyncing={isFetching} />
            <Button variant="outline" size="sm" onClick={() => setIsAvailabilityPanelOpen(true)} className="gap-1.5 hidden lg:flex">
              <UtensilsCrossed className="h-4 w-4" />
              <span>{quickAvailabilityLabel}</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { void refresh(); toast.success(t('toast.refreshed')) }} disabled={isFetching} aria-label={t('header.refresh')} title={t('header.refresh')} className="hidden lg:inline-flex h-8 w-8">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Date + status + controls row */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-2 py-2 px-3 sm:px-4 border-t border-border/50 bg-muted/40">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <DateNavigator selectedDate={selectedDate} onDateChange={setSelectedDate} onPreviousDay={goToPreviousDay} onNextDay={goToNextDay} onToday={goToToday} className="min-w-0" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSearchOpen((open) => !open)}
              aria-expanded={isSearchOpen}
              className={cn('h-9 gap-1.5 shrink-0 bg-card', (isSearchOpen || searchActive) && 'border-primary/40 text-primary')}
            >
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">{t('search.button')}</span>
              {searchActive && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { void refresh(); toast.success(t('toast.refreshed')) }} disabled={isFetching} aria-label={t('header.refresh')} title={t('header.refresh')} className="h-9 w-9 lg:hidden shrink-0">
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsAvailabilityPanelOpen(true)} className="h-9 lg:hidden shrink-0 gap-1 px-2 bg-card">
              <UtensilsCrossed className="h-4 w-4" />
              <span className="text-xs truncate hidden sm:inline">{quickAvailabilityLabel}</span>
            </Button>
          </div>

          {/* 3-way restaurant status toggle (matches old RestaurantApp) */}
          {selectedRestaurant && (
            <RestaurantStatusToggle
              restaurantId={selectedRestaurant.id}
              className="lg:ms-auto lg:max-w-[440px]"
            />
          )}
        </div>

        {/* Collapsible search row — find an order by number, phone or name */}
        {isSearchOpen && (
          <div className="flex items-center gap-3 px-3 sm:px-4 py-2.5 border-t border-border/50 bg-muted/40">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                value={filters.searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search.placeholderAll')}
                autoFocus
                className="ps-9 pe-8 h-9 bg-card"
              />
              {searchActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  aria-label={t('filters.clearAll')}
                  className="absolute end-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            {searchActive && (
              <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                {t('filters.showingResults', { count: filteredOrders.length, total: orders.length })}
              </span>
            )}
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="p-3 sm:p-4"><OrdersSkeleton /></div>
        ) : error && orders.length === 0 ? (
          /* Full error view only when there is nothing cached to show — a
             failing background refetch must not blank out the live list. */
          <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8">
            <div className="rounded-full bg-destructive/10 p-6 mb-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('orders:fetchError.title')}</h3>
            <p className="text-muted-foreground max-w-sm mb-4">{t('orders:fetchError.description')}</p>
            <Button variant="outline" onClick={refresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('orders:fetchError.retry')}
            </Button>
          </div>
        ) : orders.length === 0 ? (
          <OrdersEmptyState date={selectedDate} />
        ) : selectedOrder && !isMobile ? (
          /* Full-page detail view (replaces kanban, like RestaurantApp OrderFragment) */
          <ScrollArea className="h-full">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <OrderDetailInline
                order={selectedOrder}
                orderConfirmationEnabled={orderConfirmationEnabled}
                onClose={closeDetail}
                onStatusChange={handleStatusChange}
                onCancelOrder={openCancelDialog}
                onConfirmOrder={handleConfirmOrder}
                onDeclineOrder={openDeclineDialog}
                isUpdating={isUpdating}
                isConfirming={isConfirming}
                isDeclining={isDeclining}
              />
            </div>
          </ScrollArea>
        ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- KANBAN_LAYOUT_ENABLED is a feature flag that will be flipped on later
        (isMobile || !KANBAN_LAYOUT_ENABLED) ? (
          /* Single-column list — the layout restaurant staff know from the
             old app. Used on mobile and on desktop while the kanban flag is off. */
          <ScrollArea className="h-full">
            <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-3">
              {filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-16">
                  <div className="rounded-full bg-muted p-5 mb-4">
                    <SearchX className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <h3 className="font-display text-lg font-bold mb-1.5">{t('filters.noResults')}</h3>
                  <p className="text-muted-foreground max-w-sm mb-4">{t('filters.noResultsDescription')}</p>
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery('')}>
                    {t('filters.clearAll')}
                  </Button>
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isSelected={state.selectedOrderId === order.id}
                    onClick={() => selectOrder(order.id)}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        ) : (
          /* Tablet + Desktop: 2-column kanban (gated on KANBAN_LAYOUT_ENABLED) */
          <div className="flex h-full">
            <KanbanColumn title={t('kanban.open', 'Offen')} count={open.length} variant="open" className="border-e">
              {open.map((order) => (
                <OrderCard key={order.id} order={order} isSelected={state.selectedOrderId === order.id} onClick={() => selectOrder(order.id)} />
              ))}
            </KanbanColumn>
            <KanbanColumn title={t('kanban.answered', 'Beantwortet')} count={answered.length} variant="answered">
              {answered.map((order) => (
                <OrderCard key={order.id} order={order} isSelected={state.selectedOrderId === order.id} onClick={() => selectOrder(order.id)} />
              ))}
            </KanbanColumn>
          </div>
        )}
      </main>

      {/* Mobile: bottom sheet */}
      {isMobile && (
        <MobileOrderDetailSheet
          order={selectedOrder} isOpen={state.isDetailSheetOpen} onClose={closeDetail}
          onStatusChange={handleStatusChange} onCancelOrder={openCancelDialog}
          orderConfirmationEnabled={orderConfirmationEnabled}
          onConfirmOrder={handleConfirmOrder} onDeclineOrder={openDeclineDialog}
          isUpdating={isUpdating} isConfirming={isConfirming} isDeclining={isDeclining}
        />
      )}

      {/* Dialogs */}
      <CancelOrderDialog order={selectedOrder ?? null} isOpen={state.isCancelDialogOpen} onClose={closeCancelDialog} onConfirm={handleCancelOrder} isLoading={isCancelling} />
      <DeclineOrderDialog order={selectedOrder ?? null} isOpen={state.isDeclineDialogOpen} onClose={closeDeclineDialog} onDecline={handleDeclineOrder} isLoading={isDeclining} />
      {selectedRestaurant && isAvailabilityPanelOpen && (
        <Suspense fallback={null}>
          <QuickAvailabilityPanel
            restaurantId={selectedRestaurant.id}
            isOpen={isAvailabilityPanelOpen}
            onClose={() => setIsAvailabilityPanelOpen(false)}
          />
        </Suspense>
      )}
    </div>
  )
}

function OrdersWorkspace({ restaurantId, restaurantName }: { restaurantId: number; restaurantName: string }) {
  const { t } = useTranslation('posOrdering')
  const { isSuperAdmin } = usePermissions()
  const setup = useQuery({
    queryKey: ['pos-setup', restaurantId],
    queryFn: async () => (await apiClient.get<PosSetup>(`/api/admin/restaurants/${restaurantId}/pos-setup`)).data,
    enabled: isSuperAdmin,
  })
  const posEnabled = isSuperAdmin && setup.data?.posEnabled === true
  const [pane, setPane] = useState<'orders' | 'entry' | 'cash'>('orders')
  const [entryMounted, setEntryMounted] = useState(false)
  const showEntry = posEnabled && pane === 'entry'
  const showCash = posEnabled && pane === 'cash'
  const cash = useQuery({ queryKey: ['pos-cash', restaurantId], queryFn: ({ signal }) => fetchPosCash(restaurantId, signal),
    enabled: posEnabled, retry: false, refetchInterval: posEnabled ? 5000 : false })
  const cashState = cash.isPending ? 'cash.stateLoading' : cash.isError ? 'cash.stateUnknown'
    : cash.data.session?.state === 'open' ? 'cash.stateOpen' : cash.data.session?.state === 'counting' ? 'cash.stateCounting' : 'cash.stateClosed'

  const switchTo = (next: 'orders' | 'entry' | 'cash') => {
    if (next === 'entry') setEntryMounted(true)
    setPane(next)
  }

  return (
    <CashRegisterContext.Provider value={posEnabled ? { status: cash.data, unavailable: cash.isError, open: () => switchTo('cash') } : null}>
    <div className="flex h-full min-h-0 flex-col">
      {posEnabled && (
        <div role="tablist" aria-label={t('workspace.label')} className="flex shrink-0 gap-2 border-b border-border/60 bg-background px-3 py-2 sm:px-4">
          {(['orders', 'entry', 'cash'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              id={`orders-workspace-tab-${value}`}
              aria-selected={pane === value}
              aria-controls={`orders-workspace-pane-${value}`}
              tabIndex={pane === value ? 0 : -1}
              onKeyDown={(event) => {
                const order = ['orders', 'entry', 'cash'] as const
                const rtl = getComputedStyle(event.currentTarget).direction === 'rtl'
                const index = order.indexOf(value)
                let next: number | null = null
                if (event.key === 'Home') next = 0
                else if (event.key === 'End') next = order.length - 1
                else if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = (index + 1) % order.length
                else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = (index - 1 + order.length) % order.length
                if (next === null) return
                event.preventDefault()
                switchTo(order[next])
                document.getElementById(`orders-workspace-tab-${order[next]}`)?.focus()
              }}
              onClick={() => switchTo(value)}
              className={cn(
                'h-12 rounded-lg border px-3 text-sm sm:px-5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                pane === value ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-accent'
              )}
            >
              {value === 'orders' ? t('workspace.overview') : value === 'entry' ? t('workspace.newOrder') : t('cash.view')}
              {value === 'cash' && <span role="img" aria-label={t(cashState)} title={t(cashState)} className="ms-2 inline-flex align-middle">
                {cashState === 'cash.stateUnknown' ? <AlertTriangle aria-hidden className="h-4 w-4" />
                  : cashState === 'cash.stateLoading' ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                    : <span aria-hidden className={cn('h-2 w-2 rounded-full', cashState === 'cash.stateOpen' ? 'bg-status-success' : cashState === 'cash.stateCounting' ? 'bg-status-warning' : 'bg-muted-foreground')} />}
              </span>}
            </button>
          ))}
        </div>
      )}
      <div
        id="orders-workspace-pane-orders"
        role={posEnabled ? 'tabpanel' : undefined}
        aria-labelledby={posEnabled ? 'orders-workspace-tab-orders' : undefined}
        className={cn('flex min-h-0 flex-1 flex-col', (showEntry || showCash) && 'hidden')}
      >
        <OrdersEditorProvider key={restaurantId} restaurantId={restaurantId} posEnabled={posEnabled}>
          <OrdersContent />
        </OrdersEditorProvider>
      </div>
      {showCash && <div id="orders-workspace-pane-cash" role="tabpanel" aria-labelledby="orders-workspace-tab-cash" className="flex min-h-0 flex-1 flex-col">
        <Suspense fallback={<div className="p-4"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
          <PosCashManagement restaurantId={restaurantId} restaurantName={restaurantName} onBack={() => switchTo('orders')} />
        </Suspense>
      </div>}
      {posEnabled && entryMounted && (
        <div
          id="orders-workspace-pane-entry"
          role="tabpanel"
          aria-labelledby="orders-workspace-tab-entry"
          className={cn('flex min-h-0 flex-1 flex-col', !showEntry && 'hidden')}
        >
          <Suspense fallback={<div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
            <PosOrderEntry restaurantId={restaurantId} restaurantName={restaurantName} onShowOrders={() => switchTo('orders')} />
          </Suspense>
        </div>
      )}
    </div>
    </CashRegisterContext.Provider>
  )
}

export function Orders() {
  const { selectedRestaurant } = useRestaurant()
  if (!selectedRestaurant) return <Navigate to="/restaurants" replace />
  return (
    <OrdersWorkspace
      key={selectedRestaurant.id}
      restaurantId={selectedRestaurant.id}
      restaurantName={selectedRestaurant.name}
    />
  )
}
